import styled, { css } from 'styled-components';
import { orange, white, darkGrey } from '../../style/colors';

const center = css`display: flex; align-items: center; justify-content: center;`;
const btnBase = css`
  cursor: pointer; border: none; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const focusRing = css`&:focus-within { border-color: ${orange}; box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.1); }`;

export const Container = styled.div`
  background: linear-gradient(to bottom, ${white} 0%, #fafafa 100%);
  border-bottom: 1px solid #e5e7eb; padding: 20px 40px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 1024px) {
    padding: 16px;
  }
`;

export const SearchSection = styled.div`
  display: flex; gap: 12px; align-items: center; max-width: 1400px; margin: 0 auto;

  @media (max-width: 1024px) {
    flex-wrap: wrap;
  }
`;

export const InputWrapper = styled.div`
  flex: 1; position: relative; ${center}; border: 1.5px solid #e5e7eb; border-radius: 12px;
  padding: 12px 18px; background-color: ${white}; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  ${focusRing}
  &:hover { border-color: #d1d5db; }
  i:first-child { color: #9ca3af; font-size: 20px; transition: color 0.2s; }
  &:focus-within i:first-child { color: ${orange}; }
`;

export const SearchInput = styled.input`
  flex: 1; border: none; outline: none; font-size: 14px; font-weight: 400;
  background: transparent; color: #111827;
  &::placeholder { color: #9ca3af; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const QuantityWrapper = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px; margin-top: -16px;
`;

export const QuantityLabel = styled.span`
  font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px;
`;

export const QuantityControl = styled.div`
  ${center}; border: 1.5px solid #e5e7eb; border-radius: 10px; background-color: ${white};
  overflow: hidden; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04); transition: border-color 0.2s;
  &:hover { border-color: #d1d5db; }
`;

export const QuantityButton = styled.button`
  ${btnBase}; ${center}; background-color: ${white}; padding: 10px 14px;
  font-size: 18px; font-weight: 500; color: #6b7280; min-width: 40px;
  &:hover:not(:disabled) { background-color: #fff5f0; color: ${orange}; }
  &:active:not(:disabled) { transform: scale(0.95); background-color: #ffe0cc; }
`;

export const QuantityDisplay = styled.div`
  padding: 10px 18px; font-size: 15px; font-weight: 600; min-width: 50px;
  text-align: center; color: #111827; border-left: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb;
`;

export const SearchButton = styled.button`
  ${btnBase}; ${center}; gap: 8px; background: linear-gradient(135deg, ${orange} 0%, #ff8c00 100%);
  color: ${white}; padding: 12px 28px; border-radius: 10px; font-size: 14px; font-weight: 600;
  white-space: nowrap; box-shadow: 0 2px 8px rgba(255, 165, 0, 0.25);
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255, 165, 0, 0.35); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { box-shadow: none; }
`;

export const AdvancedButton = styled.button`
  ${btnBase}; ${center}; gap: 8px; background: ${white}; border: 1.5px solid #e5e7eb;
  color: #6b7280; padding: 12px 20px; border-radius: 10px; font-size: 14px; font-weight: 600;
  white-space: nowrap; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  &:hover { border-color: ${orange}; color: ${orange}; background: #fff5f0;
    transform: translateY(-1px); box-shadow: 0 2px 4px rgba(255, 165, 0, 0.15); }
  &:active { transform: translateY(0); }
  i { font-size: 18px; }
`;

export const IconButton = styled.button`
  ${btnBase}; background: none; font-size: 20px; color: ${darkGrey}; padding: 5px;
  &:hover { color: ${orange}; }
`;

export const ClearButton = styled.button`
  ${btnBase}; ${center}; background: none; padding: 4px; color: #9ca3af; border-radius: 4px;
  &:hover { background: #f3f4f6; color: #6b7280; }
  &:active { transform: scale(0.95); }
  i { font-size: 18px; }
`;

export const BulkButton = styled.button`
  ${btnBase}; ${center}; background: none; padding: 6px; color: #6b7280;
  border-radius: 6px; border: 1px solid transparent;
  &:hover { background: #fff5f0; color: ${orange}; border-color: #ffe0cc; }
  &:active { transform: scale(0.95); }
  i { font-size: 18px; }
`;

export const SearchInputContainer = styled.div`position: relative; width: 100%;`;

export const HelperText = styled.div`
  position: absolute; top: 100%; left: 0; font-size: 12px; margin-top: 4px;
  color: ${p => p.error ? '#ef4444' : '#6b7280'}; ${center}; justify-content: flex-start; gap: 4px;
`;

export const LoadingSpinner = styled.i`
  animation: spin 0.8s linear infinite;
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

export const SaveButton = styled.button`
  ${btnBase}; ${center}; gap: 8px; background: ${white};
  border: 1.5px solid ${p => p.isSaved ? orange : '#e5e7eb'};
  color: ${p => p.isSaved ? orange : '#6b7280'};
  padding: 10px 16px; border-radius: 10px; font-size: 14px; font-weight: 600;
  white-space: nowrap; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  &:hover:not(:disabled) { border-color: ${orange}; color: ${orange}; background: #fff5f0;
    transform: translateY(-1px); box-shadow: 0 2px 4px rgba(255, 165, 0, 0.15); }
  &:active:not(:disabled) { transform: translateY(0); }
  i { font-size: 18px; }
`;

export const DimensionSuggestion = styled.div`
  position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px;
  padding: 10px 16px; background: #fff5f0; border: 1px solid #ffe0cc; border-radius: 8px;
  font-size: 13px; color: #92400e; ${center}; justify-content: flex-start; gap: 8px; cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #ffedd5; border-color: ${orange}; }
  i { color: ${orange}; font-size: 16px; }
  strong { color: ${orange}; font-weight: 600; }
`;

export const ToggleButton = styled.button`
  ${btnBase}; ${center}; padding: 6px 8px; border-radius: 6px;
  background: ${p => p.enabled ? '#fff8f0' : 'transparent'};
  border: 1px solid ${p => p.enabled ? '#ffd7a8' : 'transparent'};
  color: ${p => p.enabled ? orange : '#9ca3af'};
  &:hover { background: ${p => p.enabled ? '#ffe8cc' : '#f3f4f6'}; color: ${p => p.enabled ? orange : '#6b7280'}; }
  &:active { transform: scale(0.95); }
  i { font-size: 18px; }
  & + & { margin-left: 8px; }
`;

export const VehicleSearchBadge = styled.div`
  ${center}; gap: 10px; flex: 1; padding: 8px 14px;
  background: linear-gradient(135deg, #fff8f0 0%, #fff5eb 100%);
  border: 1.5px solid #ffd7a8; border-radius: 10px;
  color: #92400e; font-size: 13px; font-weight: 500;
  
  .vehicle-icon {
    ${center}; width: 32px; height: 32px; background: ${orange};
    border-radius: 8px; color: ${white}; flex-shrink: 0;
    i { font-size: 16px; }
  }
  
  .vehicle-info {
    display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;
    .vehicle-title {
      font-weight: 600; color: #78350f; white-space: nowrap;
      overflow: hidden; text-overflow: ellipsis;
    }
    .vehicle-details {
      font-size: 12px; color: #a16207; white-space: nowrap;
      overflow: hidden; text-overflow: ellipsis;
    }
  }
  
  .vehicle-count {
    ${center}; background: ${orange}; color: ${white};
    padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;
    white-space: nowrap;
  }
`;
